import { ArrowsAltOutlined } from '@ant-design/icons';
import { WaterbodyDetail } from '@omniafishing/core';
import React, { useState } from 'react';
import { useDimensions } from '../../hooks/use_dimensions';
import { useQueryString } from '../../hooks/use_query_string';
import { fitBounds } from '../../lib/fit_bounds';
import { OmniaUrls } from '../../lib/urls';
import { ClickKey_LakePage, WebAnalytics } from '../../lib/web_analytics';
import { RoutePaths } from '../../routes';
import {
  BASE_LAYER,
  CORE_LAYER,
  LAYER_TYPES,
  MapLayerState,
  WEATHER_LAYER,
} from '../map/map_types';
import { MapDefault } from '../map_default/map_default';
import { OmniaLinkButton } from '../omnia_button/omnia_link_button';
import { OmniaLink } from '../omnia_link/omnia_link';
import SvgWindLegend from '../svg/wind_legend';
import { WaterbodyDetailQueryParams } from './waterbody_detail';
import styles from './waterbody_detail.less';

interface WaterbodyDetailMapProps {
  waterbody: WaterbodyDetail;
}

export const WaterbodyDetailMap = (props: WaterbodyDetailMapProps) => {
  const { waterbody } = props;
  const { lat, lng, bbox } = waterbody;
  const [measureRef, bounds] = useDimensions({ onResize: true });

  const [maxBounds, setMaxBounds] =
    useState<[{ lat: number; lng: number }, { lat: number; lng: number }]>();

  let zoom = 13;
  if (bbox && bounds.width && bounds.height) {
    zoom = fitBounds([bbox[0], bbox[2]], {
      width: bounds.width,
      height: bounds.height,
    }).zoom;
  }

  // the lake lat lng is not always the midpoint of the bbox, midpoint works better for restricting the size
  const bboxMidPoint =
    bbox != null
      ? {
          lng: (bbox[0][0] + bbox[2][0]) / 2,
          lat: (bbox[0][1] + bbox[2][1]) / 2,
        }
      : {
          lng,
          lat,
        };

  const { getCurrentQuery } = useQueryString();
  const currentQuery = getCurrentQuery<WaterbodyDetailQueryParams>();
  const webAnalyticsLakeClickArgs = {
    season: currentQuery.season_group,
    species: currentQuery.species,
    technique: currentQuery.technique,
    waterbody_slug: waterbody.url_slug,
  };

  const webAnalyticsLakePageClick = (type: ClickKey_LakePage) => {
    return () => WebAnalytics.lakePageClick(type, webAnalyticsLakeClickArgs);
  };

  const mapLayerState: Partial<MapLayerState> = {
    [LAYER_TYPES.CORE]: CORE_LAYER.OMNIA_WATER_TEMP,
    [LAYER_TYPES.WEATHER]: [WEATHER_LAYER.WIND],
    [LAYER_TYPES.BASE]: BASE_LAYER.SATELLITE,
  };

  return (
    <div className={styles.mapWrapper}>
      <div className={styles.map} ref={measureRef}>
        {bounds.width > 0 && (
          <>
            <MapDefault
              backToMapLink
              zoom={zoom}
              mapLayerStateOverrides={mapLayerState}
              latitude={bboxMidPoint.lat}
              longitude={bboxMidPoint.lng}
              maxBounds={maxBounds}
              disablePinFetch
              hideGeocoder
              navControl={false}
              restrictAllMovement
              onBackToMapClick={webAnalyticsLakePageClick('[map].(expand)')}
              onPinClick={webAnalyticsLakePageClick('[map].(pin)')}
              onSatelliteChange={webAnalyticsLakePageClick('[map].(satellite)')}
              onGeocoderResultClick={webAnalyticsLakePageClick('[map].(search)')}
              onLoad={(mapBounds) => {
                setMaxBounds([
                  {
                    lat: mapBounds.s,
                    lng: mapBounds.w,
                  },
                  {
                    lat: mapBounds.n,
                    lng: mapBounds.e,
                  },
                ]);
              }}
            />
            <OmniaLinkButton
              to={OmniaUrls.map({ lat, lng, zoom: zoom - 2, waterbodySlug: waterbody.url_slug })}
              kind={'tertiary'}
              size="md"
              className={styles.exploreMapButton}
            >
              <ArrowsAltOutlined />
              Explore Full Map
            </OmniaLinkButton>
          </>
        )}
      </div>
      <div className={styles.mapLegend}>
        <div className={styles.legendItem}>
          <div className={styles.legendTop}>
            <span>SLOW</span>
            <strong>
              Wind <span>(mph)</span>
            </strong>
            <span>FAST</span>
          </div>
          <span>
            <SvgWindLegend />
          </span>
          <div className={styles.legendBottom}>
            <span>0</span>
            <span>10</span>
            <span>20</span>
            <span>40</span>
            <span>60</span>
            <span>80</span>
            <span>100</span>
          </div>
        </div>

        <div className={styles.windCallOut}>
          <OmniaLink
            underline
            to={RoutePaths.PREMIUM_PRO}
            onClick={() =>
              WebAnalytics.proButtonClicked({
                location: 'waterbody_detail_map',
                type: 'learn_more',
                style: 'link',
              })
            }
          >
            Learn More About <br />
            Omnia PRO Weather Layers
          </OmniaLink>
        </div>
      </div>
    </div>
  );
};
